import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { Alert, Button, Drawer, Form, Input, Modal, Select, Table, Typography } from 'antd';
import {
  CardTitle,
  DetailList,
  ListToolbar,
  PageHeader,
  RowActions,
  Section,
  SectionCard,
  StatusTag,
  TabBar,
  fmtDateTime,
  tablePagination,
  useFlatPage
} from '../components/ui';
import { IconInfo, IconPlus } from '../components/ui/icons';
import notify from "../utils/notify";

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const MaintenanceDashboard = () => {
  useFlatPage();
  const { authData } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [staff, setStaff] = useState([]);
  const [assignForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [createForm] = Form.useForm();
  const [apiErrors, setApiErrors] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [roomMaintenanceStatus, setRoomMaintenanceStatus] = useState({});

  const [activeTab, setActiveTab] = useState('all');
  const [rooms, setRooms] = useState([]);

  const statusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'InProgress', label: 'In progress' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' },
  ];

  const priorityOptions = [
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Emergency', label: 'Emergency' },
  ];

  const checkRoomMaintenance = async (roomNumber) => {
    if (!roomNumber) return false;
    
    try {
      const response = await axiosInstance.get(
        `/Maintenance/CheckRoomMaintenance`, 
        {
          params: { 
            roomNumber, 
            hotelId: authData.hotelId 
          }
        }
      );
      return response.data.isUnderMaintenance;
    } catch (error) {
      console.error('Error checking room status:', error);
      return false;
    }
  };

  useEffect(() => {
    if (authData?.hotelId) {
      fetchTasks();
      fetchMaintenanceStaff();
      fetchRooms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.hotelId]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/Maintenance/GetAllMaintenanceTasks?hotelId=${authData.hotelId}`);
      const tasksWithKeys = (response.data || []).map(task => ({
        ...task,
        key: task.maintenanceTaskId,
      }));

      setTasks(tasksWithKeys);
      setApiErrors(null);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setApiErrors(err.response?.data);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to load maintenance tasks"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceStaff = async () => {
    try {
      const response = await axiosInstance.get("/User/WithPermission", { 
        params: { 
          hotelId: authData.hotelId,
          permission: "MaintenanceAssignment" 
        } 
      });
      setStaff(response.data.users || []);
    } catch (err) {
      console.error("Error fetching staff:", err);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to load maintenance staff"
      });
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axiosInstance.get(`/Rooms/all/${authData.hotelId}`);
      setRooms(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error fetching rooms:", err);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to load rooms"
      });
    }
  };

  const handleAssignTask = async (values) => {
    try {
      await axiosInstance.post('/Maintenance/Assign', {
        ...values,
        maintenanceTaskId: currentTask.maintenanceTaskId,
        hotelId: authData.hotelId
      });
      
      const updatedTasks = tasks.map(task => 
        task.maintenanceTaskId === currentTask.maintenanceTaskId
          ? {
              ...task,
              assignedTo: staff.find(user => user.userId === values.userId),
              assignedToUserId: values.userId,
              assignmentNotes: values.assignmentNotes
            }
          : task
      );
      
      setTasks(updatedTasks);
      notify.success({
        title: 'Success',
        description: 'Task assigned successfully'
      });
      setAssignModalOpen(false);
    } catch (err) {
      console.error("Error assigning task:", err);
      setApiErrors(err.response?.data);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to assign task"
      });
    }
  };

  const handleUnassignTask = async () => {
    try {
      await axiosInstance.post('/Maintenance/Unassign', {
        maintenanceTaskId: currentTask.maintenanceTaskId,
        hotelId: authData.hotelId
      });
      
      const updatedTasks = tasks.map(task => 
        task.maintenanceTaskId === currentTask.maintenanceTaskId
          ? {
              ...task,
              assignedTo: null,
              assignedToUserId: null,
              assignmentNotes: null
            }
          : task
      );
      
      setTasks(updatedTasks);
      notify.success({
        title: 'Success',
        description: 'Task unassigned successfully'
      });
      setUnassignModalOpen(false);
    } catch (err) {
      console.error("Error unassigning task:", err);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to unassign task"
      });
    }
  };

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      
      // Prepare form data (convert enums if needed)
      const formData = {
        ...values,
        status: values.status ? values.status : undefined, // Already in correct format
        priority: values.priority || undefined
      };

      // Make the API call
      const response = await axiosInstance.put(
        `/Maintenance/${currentTask.maintenanceTaskId}`,
        formData,
        {
          params: { hotelId: authData.hotelId },
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Update local state
      const updatedTasks = tasks.map(task => 
        task.maintenanceTaskId === currentTask.maintenanceTaskId
          ? { ...task, ...response.data } // Use data from server response
          : task
      );
      
      setTasks(updatedTasks);
      
      notify.success({
        title: 'Success',
        description: 'Task updated successfully'
      });
      setEditModalOpen(false);
    } catch (err) {
      console.error("Error updating task:", err);
      notify.error({
        title: "Error",
        description: err.response?.data?.message || "Failed to update task"
      });
    }
  };

  const handleCreateTask = async () => {
    try {
      const values = await createForm.validateFields();
      
      // Validate room maintenance status
      if (values.roomNumber) {
        const isUnderMaintenance = await checkRoomMaintenance(values.roomNumber);
        if (isUnderMaintenance) {
          notify.error({
            title: 'Room Unavailable',
            description: 'This room is already under maintenance. Please select another room or location.',
          });
          return;
        }
      }

      const taskData = {
        title: values.title,
        description: values.description,
        roomNumber: values.roomNumber || null,
        location: values.location || null,
        priority: values.priority,
        reportedBy: authData.firstName || authData.email,
        hotelId: authData.hotelId,
        requestedByUserId: authData.userId
      };

      // Remove null values
      Object.keys(taskData).forEach(key => {
        if (taskData[key] === null || taskData[key] === undefined) {
          delete taskData[key];
        }
      });

      setLoading(true);
      const response = await axiosInstance.post('/Maintenance/CreateTask', taskData);
      
      const newTask = {
        ...response.data,
        key: response.data.maintenanceTaskId,
      };
      
      setTasks(prev => [...prev, newTask]);
      
      notify.success({
        title: 'Task Created',
        description: 'Maintenance task has been successfully created',
      });
      
      setCreateModalOpen(false);
      setRoomMaintenanceStatus({});
      createForm.resetFields();
    } catch (error) {
      console.error("Error creating task:", error);
      
      let errorMessage = "Failed to create task";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = Object.values(error.response.data.errors)
          .flat()
          .join(', ');
      }
      
      notify.error({
        title: 'Creation Failed',
        description: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStatusTag = (status) => {
    const config = statusOptions.find(opt => opt.value === status);
    return <StatusTag status={status}>{config?.label || status}</StatusTag>;
  };

  const renderPriorityTag = (priority) => <StatusTag status={priority}>{priority || 'Unknown'}</StatusTag>;

  const handleDeleteTask = async (taskId) => {
    try {
      // First get the task details to check if it has a roomId
      const taskToDelete = tasks.find(t => t.maintenanceTaskId === taskId);
      const hasRoom = taskToDelete?.roomId;

      // Delete the task
      const response = await axiosInstance.delete(
        `/Maintenance/DeleteTask/${taskId}`,
        {
          params: { 
            hotelId: authData.hotelId,
            timestamp: Date.now() // Cache buster
          },
          validateStatus: (status) => 
            status === 204 || status === 404 // Accept both success and not found
        }
      );

      if (response.status === 204) {
        // Success case - update room status if needed
        if (hasRoom) {
          try {
            await axiosInstance.put(`/Rooms/UpdateStatus/${taskToDelete.roomId}`, {
              status: 'Available', // Or whatever default status you want
              hotelId: authData.hotelId
            });
          } catch (roomError) {
            console.error("Error updating room status:", roomError);
            // Don't fail the whole operation if room update fails
            notify.warning({
              title: 'Task Deleted',
              description: 'Task was deleted but room status could not be updated',
            });
          }
        }

        // Update local state
        setTasks(tasks.filter(t => t.maintenanceTaskId !== taskId));
        notify.success({
          title: 'Task Deleted',
          description: 'The maintenance task was successfully removed',
        });
      } else {
        // 404 case
        notify.warning({
          title: 'Task Not Found',
          description: response.data?.message || 'The task may have already been deleted',
        });
        fetchTasks(); // Refresh list
      }
    } catch (err) {
      console.error("Delete error:", err);
      notify.error({
        title: 'Deletion Failed',
        description: err.response?.data?.message || 'Could not delete the task',
      });
    }
  };

  const counts = useMemo(() => {
    const c = { Pending: 0, InProgress: 0, Completed: 0, Cancelled: 0 };
    tasks.forEach(x => {
      if (c[x.status] != null) c[x.status] += 1;
    });
    return c;
  }, [tasks]);

  const tabs = [
    { key: 'All', label: 'All', count: tasks.length },
    ...statusOptions.map(o => ({ key: o.value, label: o.label, count: counts[o.value] || 0 })),
  ];

  const visibleTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return tasks.filter(task => {
      if (filterStatus !== 'All' && task.status !== filterStatus) return false;
      if (!q) return true;
      return (
        (task.title || '').toLowerCase().includes(q) ||
        (task.description || '').toLowerCase().includes(q) ||
        String(task.roomNumber || '').toLowerCase().includes(q) ||
        (task.location || '').toLowerCase().includes(q)
      );
    });
  }, [tasks, filterStatus, searchQuery]);

  const openDetails = (record) => {
    setCurrentTask(record);
    setDetailsOpen(true);
  };

  const openEdit = (record) => {
    setCurrentTask(record);
    editForm.setFieldsValue({
      title: record.title,
      description: record.description,
      status: record.status,
      priority: record.priority,
      notes: record.notes
    });
    setEditModalOpen(true);
  };

  const openAssign = (record) => {
    setCurrentTask(record);
    assignForm.setFieldsValue({
      userId: record.assignedToUserId || null,
      assignmentNotes: record.assignmentNotes || ''
    });
    setAssignModalOpen(true);
  };

  const columns = [
    {
      title: 'Task',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.maintenanceTaskId - b.maintenanceTaskId,
      render: (text, record) => (
        <div className="hms-cell-stack">
          <span className="hms-cell-main">{text}</span>
          <span className="hms-cell-sub">#{record.maintenanceTaskId}</span>
        </div>
      )
    },
    {
      title: 'Room / location',
      key: 'location',
      render: (_, record) => record.roomNumber || record.location || <span className="hms-muted">—</span>
    },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 140, render: renderStatusTag },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', width: 130, render: renderPriorityTag },
    {
      title: 'Assigned to',
      key: 'assignedTo',
      render: (_, record) =>
        record.assignedTo ? (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{record.assignedTo.userName}</span>
            <span className="hms-cell-sub">{record.assignedTo.role}</span>
          </div>
        ) : (
          <span className="hms-muted">Not assigned</span>
        )
    },
    {
      title: '',
      key: 'actions',
      fixed: 'right',
      align: 'right',
      width: 150,
      render: (_, record) => (
        <span onClick={(e) => e.stopPropagation()}>
          <RowActions
            actions={[
              { key: 'edit', label: 'Edit task', icon: 'edit', onClick: () => openEdit(record) },
              record.assignedTo
                ? {
                    key: 'unassign',
                    label: 'Unassign task',
                    icon: 'unassign',
                    onClick: () => {
                      setCurrentTask(record);
                      setUnassignModalOpen(true);
                    }
                  }
                : { key: 'assign', label: 'Assign task', icon: 'assign', onClick: () => openAssign(record) },
              {
                key: 'delete',
                label: 'Delete task',
                icon: 'delete',
                danger: true,
                confirm: 'Delete this task?',
                okText: 'Delete',
                onClick: () => handleDeleteTask(record.maintenanceTaskId)
              }
            ]}
          />
        </span>
      )
    }
  ];

  const openCreate = () => {
    createForm.resetFields();
    setRoomMaintenanceStatus({});
    setCreateModalOpen(true);
  };

  return (
    <div className="hms-page md-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setInfoModalOpen(true)}>
              Guide
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={openCreate}>
              Create task
            </Button>
          </>
        }
      />

      {apiErrors && (
        <Alert
          title="Something went wrong"
          description={apiErrors.message || 'An error occurred'}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          onClose={() => setApiErrors(null)}
        />
      )}

      <SectionCard
        flush
        title={<CardTitle count={tasks.length}>Maintenance tasks</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search tasks' }}
            onRefresh={fetchTasks}
            loading={loading}
          />
        }
      >
        <TabBar tabs={tabs} value={filterStatus} onChange={setFilterStatus} />

        <Table
          className="hms-table"
          rowKey="maintenanceTaskId"
          columns={columns}
          dataSource={visibleTasks}
          loading={loading}
          pagination={tablePagination('task')}
          scroll={{ x: 900 }}
          onRow={(record) => ({ onClick: () => openDetails(record), style: { cursor: 'pointer' } })}
          locale={{ emptyText: <div className="hms-empty">No maintenance tasks in this view</div> }}
        />
      </SectionCard>

      {/* Task details */}
      <Drawer title="Task details" open={detailsOpen} onClose={() => setDetailsOpen(false)} size={560} destroyOnHidden>
        {currentTask && (
          <>
            <div className="hms-summary">
              <div>
                <div className="hms-summary-title">{currentTask.title}</div>
                <div className="hms-summary-sub">Task #{currentTask.maintenanceTaskId}</div>
              </div>
              {renderStatusTag(currentTask.status)}
            </div>

            <DetailList
              items={[
                { label: 'Priority', value: renderPriorityTag(currentTask.priority) },
                { label: 'Description', value: currentTask.description },
                { label: 'Room', value: currentTask.roomNumber },
                { label: 'Location', value: currentTask.location },
                {
                  label: 'Assigned to',
                  value: currentTask.assignedTo
                    ? `${currentTask.assignedTo.userName}${currentTask.assignedTo.role ? ` (${currentTask.assignedTo.role})` : ''}`
                    : 'Not assigned'
                },
                { label: 'Assignment notes', value: currentTask.assignmentNotes },
                { label: 'Internal notes', value: currentTask.notes },
                { label: 'Created', value: currentTask.createdDate ? fmtDateTime(currentTask.createdDate) : null },
                { label: 'Completed', value: currentTask.completedDate ? fmtDateTime(currentTask.completedDate) : null }
              ]}
            />

            <div className="hms-form-actions" style={{ marginTop: 20 }}>
              <Button
                onClick={() => {
                  setDetailsOpen(false);
                  openEdit(currentTask);
                }}
              >
                Edit task
              </Button>
            </div>
          </>
        )}
      </Drawer>

      {/* Assign */}
      <Modal
        title={`Assign task #${currentTask?.maintenanceTaskId || ''}`}
        open={assignModalOpen}
        onCancel={() => setAssignModalOpen(false)}
        footer={null}
        width={480}
        forceRender
      >
        <Form form={assignForm} layout="vertical" onFinish={handleAssignTask}>
          <Form.Item label="Assign to" name="userId" rules={[{ required: true, message: 'Please select a staff member' }]}>
            <Select
              placeholder={staff.length === 0 ? 'No staff available' : 'Select maintenance staff'}
              disabled={staff.length === 0}
            >
              {staff
                .filter((user, index, self) => index === self.findIndex(u => u.userId === user.userId))
                .map(user => (
                  <Option key={user.userId} value={user.userId}>
                    {user.userName} ({user.role})
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item label="Assignment notes" name="assignmentNotes">
            <TextArea rows={3} placeholder="Any special instructions" />
          </Form.Item>

          <div className="hms-form-actions">
            <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Assign task
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Unassign */}
      <Modal
        title={`Unassign task #${currentTask?.maintenanceTaskId || ''}`}
        open={unassignModalOpen}
        onCancel={() => setUnassignModalOpen(false)}
        onOk={handleUnassignTask}
        okText="Unassign"
        width={480}
        destroyOnHidden
      >
        <p className="hms-modal-note">
          Unassign this task from {currentTask?.assignedTo?.userName || 'the current assignee'}?
        </p>
        {currentTask?.assignmentNotes && (
          <Alert title="Assignment notes" description={currentTask.assignmentNotes} type="info" showIcon />
        )}
      </Modal>

      {/* Edit */}
      <Modal
        title={`Edit task #${currentTask?.maintenanceTaskId || ''}`}
        open={editModalOpen}
        onCancel={() => {
          editForm.resetFields();
          setEditModalOpen(false);
        }}
        onOk={handleEditSubmit}
        okText="Save changes"
        confirmLoading={loading}
        width={600}
        forceRender
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: 'Please enter a title' },
              { max: 100, message: 'Title cannot exceed 100 characters' }
            ]}
          >
            <Input placeholder="Task title" showCount maxLength={100} />
          </Form.Item>

          <Form.Item name="description" label="Description" rules={[{ max: 500, message: 'Description cannot exceed 500 characters' }]}>
            <TextArea rows={4} placeholder="Detailed description of the task" showCount maxLength={500} />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Please select a status' }]}>
              <Select placeholder="Select status" options={statusOptions} />
            </Form.Item>

            <Form.Item name="priority" label="Priority" rules={[{ required: true, message: 'Please select a priority' }]}>
              <Select placeholder="Select priority" options={priorityOptions} />
            </Form.Item>
          </div>

          <Form.Item name="notes" label="Internal notes" tooltip="Visible only to maintenance staff and administrators">
            <TextArea rows={3} placeholder="Additional notes or instructions" showCount maxLength={1000} />
          </Form.Item>

          {currentTask && (currentTask.roomNumber || currentTask.location || currentTask.assignedTo) && (
            <DetailList
              items={[
                currentTask.roomNumber ? { label: 'Room', value: currentTask.roomNumber } : null,
                currentTask.location ? { label: 'Area', value: currentTask.location } : null,
                currentTask.assignedTo
                  ? { label: 'Assigned to', value: `${currentTask.assignedTo.userName} (${currentTask.assignedTo.role})` }
                  : null
              ]}
            />
          )}
        </Form>
      </Modal>

      {/* Create (handleCreateTask validates, saves, closes and shows its own result) */}
      <Modal
        title="Create maintenance task"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setRoomMaintenanceStatus({});
          createForm.resetFields();
        }}
        onOk={handleCreateTask}
        okText="Create task"
        confirmLoading={loading}
        width={600}
        forceRender
      >
        <Form form={createForm} layout="vertical" initialValues={{ priority: 'Medium' }}>
          <Form.Item
            name="title"
            label="Title"
            rules={[
              { required: true, message: 'Please enter a title' },
              { min: 3, message: 'Title needs at least 3 characters' },
              { max: 100, message: 'Title cannot exceed 100 characters' }
            ]}
          >
            <Input placeholder="Task title" showCount maxLength={100} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: 'Please enter a description' },
              { min: 10, message: 'Description needs at least 10 characters' },
              { max: 500, message: 'Description cannot exceed 500 characters' }
            ]}
          >
            <TextArea rows={4} placeholder="Detailed description of the maintenance task" showCount maxLength={500} />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item
              name="roomNumber"
              label="Room number (optional)"
              help={roomMaintenanceStatus[createForm.getFieldValue('roomNumber')] ? 'This room is already under maintenance' : null}
              validateStatus={roomMaintenanceStatus[createForm.getFieldValue('roomNumber')] ? 'error' : null}
            >
              <Input
                placeholder="e.g. 101"
                onChange={async (e) => {
                  const roomNumber = e.target.value;
                  if (roomNumber) {
                    const isUnderMaintenance = await checkRoomMaintenance(roomNumber);
                    setRoomMaintenanceStatus(prev => ({ ...prev, [roomNumber]: isUnderMaintenance }));
                    createForm.setFieldsValue({ location: `Room ${roomNumber}` });
                  } else {
                    setRoomMaintenanceStatus({});
                    if (createForm.getFieldValue('location')?.startsWith('Room ')) {
                      createForm.setFieldsValue({ location: '' });
                    }
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              name="location"
              label="Location"
              rules={[
                { required: true, message: 'Please enter a location' },
                { min: 3, message: 'Location needs at least 3 characters' }
              ]}
            >
              <Input
                placeholder="e.g. Lobby, Pool area"
                onChange={(e) => {
                  if (!e.target.value.startsWith('Room ')) {
                    createForm.setFieldsValue({ roomNumber: '' });
                    setRoomMaintenanceStatus({});
                  }
                }}
              />
            </Form.Item>
          </div>

          <Form.Item name="priority" label="Priority" rules={[{ required: true, message: 'Please select a priority' }]}>
            <Select placeholder="Select priority" options={priorityOptions} />
          </Form.Item>

          <Form.Item name="notes" label="Internal notes" tooltip="Visible only to maintenance staff and administrators">
            <TextArea rows={3} placeholder="Additional notes or instructions" showCount maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Guide */}
      <Modal
        title="Maintenance guide"
        open={infoModalOpen}
        onCancel={() => setInfoModalOpen(false)}
        width={640}
        destroyOnHidden
        footer={[
          <Button key="perm" onClick={() => { navigate('/hotels/PermissionManager'); setInfoModalOpen(false); }}>
            Permission manager
          </Button>,
          <Button key="ok" type="primary" onClick={() => setInfoModalOpen(false)}>
            Got it
          </Button>
        ]}
      >
        <p className="hms-modal-note">Track and manage every maintenance task in your hotel.</p>

        <Section title="What you can do">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Create, edit and delete maintenance tasks</li>
            <li>Assign tasks to maintenance staff</li>
            <li>Filter by status and search by title, room or location</li>
          </ul>
        </Section>

        <Section title="Assigning tasks">
          <p style={{ margin: '0 0 8px' }}>
            Staff must have the <Text strong>Maintenance</Text> role or the <Text strong>MaintenanceAssignment</Text> permission,
            belong to this hotel and have an active account. If nobody appears in the assign list, check those three things in the
            Permission manager.
          </p>
        </Section>

        <Section title="Priority levels">
          <DetailList
            items={[
              { label: 'Low', value: "Minor issues that don't affect operations" },
              { label: 'Medium', value: 'Issues that should be addressed soon' },
              { label: 'High', value: 'Critical issues affecting functionality' },
              { label: 'Emergency', value: 'Immediate attention required' }
            ]}
          />
        </Section>

        <Section title="Permissions used">
          <DetailList
            items={[
              { label: 'Maintenance.View', value: 'View maintenance tasks and the dashboard' },
              { label: 'Maintenance.Create', value: 'Create new tasks' },
              { label: 'Maintenance.Edit', value: 'Modify existing tasks' },
              { label: 'Maintenance.Assign', value: 'Assign tasks to staff' },
              { label: 'Maintenance.Unassign', value: 'Unassign tasks from staff' }
            ]}
          />
        </Section>
      </Modal>
    </div>
  );
};

export default MaintenanceDashboard;
